/**
 * Funções utilitárias de formatação
 * Centraliza formatadores reutilizáveis em todo o sistema
 * 
 * @version 1.0.0
 * RELEASE: 2026-01-10
 */

/**
 * Formata o código do negócio (8 primeiros caracteres do UUID em maiúsculas)
 * Formato: N.XXXXXXXX
 * 
 * @param dealId - UUID completo do negócio
 * @returns Código formatado: "N.A1B2C3D4"
 * 
 * @example
 * formatDealCode('a1b2c3d4-e5f6-7890-abcd-ef1234567890')
 * // Retorna: "N.A1B2C3D4"
 */
export function formatDealCode(dealId: string): string {
  if (!dealId || dealId.length < 8) return 'N.--------';
  return `N.${dealId.substring(0, 8).toUpperCase()}`;
}

/**
 * Formata o código do cliente (8 primeiros caracteres do UUID em maiúsculas)
 * Formato: C.XXXXXXXX
 * 
 * @param customerId - UUID completo do cliente
 * @returns Código formatado: "C.A1B2C3D4"
 */
export function formatCustomerCode(customerId: string): string {
  if (!customerId || customerId.length < 8) return 'C.--------';
  return `C.${customerId.substring(0, 8).toUpperCase()}`;
}

/**
 * Formata qualquer UUID para exibição (8 primeiros caracteres em maiúsculas)
 * 
 * @param uuid - UUID completo
 * @param prefix - Prefixo opcional (ex: "N.", "C.", "P.")
 * @returns Código formatado com prefixo
 */
export function formatUuidShort(uuid: string, prefix: string = ''): string {
  if (!uuid || uuid.length < 8) return `${prefix}--------`;
  return `${prefix}${uuid.substring(0, 8).toUpperCase()}`;
}

/**
 * Formata valor como moeda brasileira compacta
 * Usa sufixos K (milhares) e M (milhões) para valores grandes
 * Preserva 2 casas decimais para maior precisão
 * 
 * @param value - Valor numérico a ser formatado
 * @param showPrefix - Se deve incluir "R$ " (default: true)
 * @returns Valor formatado: "R$ 1.49K", "R$ 19.75K", "R$ 2.31M"
 * 
 * @example
 * formatCompactCurrency(1490)        // "R$ 1.49K"
 * formatCompactCurrency(19750)       // "R$ 19.75K"
 * formatCompactCurrency(2310000)     // "R$ 2.31M"
 * formatCompactCurrency(100000)      // "R$ 100K"
 * formatCompactCurrency(1490, false) // "1.49K"
 * formatCompactCurrency(750)         // "R$ 750"
 * 
 * @version 1.1.0
 * RELEASE: 2026-01-12
 */
export function formatCompactCurrency(value: number, showPrefix: boolean = true): string {
  const prefix = showPrefix ? 'R$ ' : '';
  
  // Milhões: 2 casas decimais, remove zeros desnecessários
  if (value >= 1000000) {
    const formatted = (value / 1000000).toFixed(2);
    // Remove zeros desnecessários: 2.00M → 2M, 2.10M → 2.1M
    const cleaned = formatted.replace(/\.?0+$/, '');
    return `${prefix}${cleaned}M`;
  }
  
  // Milhares: 2 casas decimais, remove zeros desnecessários
  if (value >= 1000) {
    const formatted = (value / 1000).toFixed(2);
    // Remove zeros desnecessários: 19.00K → 19K, 19.50K → 19.5K
    const cleaned = formatted.replace(/\.?0+$/, '');
    return `${prefix}${cleaned}K`;
  }
  
  // Valores pequenos: sem casas decimais
  return `${prefix}${value.toFixed(0)}`;
}

/**
 * Formata valor como moeda brasileira completa
 * Usa Intl.NumberFormat para formatação padrão brasileiro
 * 
 * @param value - Valor numérico a ser formatado
 * @returns Valor formatado: "R$ 1.490,00"
 * 
 * @example
 * formatFullCurrency(1490)    // "R$ 1.490"
 * formatFullCurrency(19750)   // "R$ 19.750"
 * 
 * @version 1.0.0
 * RELEASE: 2026-01-12
 */
export function formatFullCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
}
